import { ReactNode } from "react";
import { Button } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import useDeleteButtonEnabled from "../Button/useDeleteButtonEnabled.tsx";
import { IconDelete, IconEdit, IconLock, IconUnlock } from "../Icon/Icon.tsx";
import DeleteModal from "../Modal/DeleteModal.tsx";
import TooltipCustom from "../Tooltip/Tooltip.tsx";
import { actionColumnType, columnType } from "./QueryTable.tsx";
import { ActionButton } from "./TableActionColumn.tsx";

export const ACTION_BUTTON_SIZE = 28;

const EditColumn = ({
  to,
  title = true,
  disabled = false,
  disable = () => false,
  textDisable = "",
  ...option
}: EditColumnType) => ({
  // eslint-disable-next-line react/display-name
  Cell: (row: any) => {
    return (
      <>
        <TooltipCustom
          tooltipText={!(disable(row) || disabled) ? "Modifier" : textDisable}
          tooltipId={row.value}
        >
          <Button
            variant="link"
            href={to(row.value)}
            disabled={disabled || disable(row)}
          >
            <IconEdit />
            {title && <>&nbsp;Modifier</>}
          </Button>
        </TooltipCustom>
      </>
    );
  },
  width: title ? 140 : 90,
  ...option,
});

type EditColumnType = {
  to: (id: string) => any;
  title?: boolean;
  accessor: string;
  textDisable?: string;
  disabled: boolean;
  disable: (t?: any) => boolean;
};

export default EditColumn;

export const LinkColumn = ({
  to,
  title = "",
  icon,
  disabled = false,
  disable = () => false,
  tooltipText = "",
  textDisable = "",
  ...option
}: LinkColumnType) => ({
  // eslint-disable-next-line react/display-name
  Cell: (row: any) => {
    const titleIsNotEmpty = title?.trim().length > 0;
    return (
      <>
        <TooltipCustom
          tooltipText={!(disable(row) || disabled) ? tooltipText : textDisable}
          tooltipId={row.value}
        >
          <Button
            variant="link"
            href={to(row.value)}
            disabled={disabled || disable(row)}
          >
            {icon}
            {titleIsNotEmpty && title}
          </Button>
        </TooltipCustom>
      </>
    );
  },
  width: title ? 140 : 90,
  ...option,
});

type LinkColumnType = {
  to: (id: string) => any;
  title?: string;
  icon: ReactNode;
  accessor: string;
  textDisable?: string;
  disabled: boolean;
  disable: (t?: any) => boolean;
  tooltipText?: string;
};

type DeleteColumnType = {
  path: string;
  reload?: () => void;
  title: boolean;
  accessor: string;
  disabled?: boolean;
  disable?: (t?: any) => boolean;
  textDisable?: string;
};
export const DeleteColumn = ({
  path,
  reload,
  title = true,
  disabled = false,
  disable = () => false,
  textDisable = "",
  ...options
}: DeleteColumnType) => ({
  // eslint-disable-next-line react/display-name
  Cell: (row: { value: string }) => {
    const {
      visible,
      ref,
      isDeleteEnabled,
      openDeleteModal,
      closeDeleteModalCancel,
      closeDeleteModalError,
      closeDeleteModalSuccess,
    } = useDeleteButtonEnabled();
    const query = `${path}${row.value}`;

    const isDisabled = disabled || disable(row) || !isDeleteEnabled;
    const computedTextDisable = !isDeleteEnabled
      ? "Élément déjà supprimé"
      : textDisable;
    return (
      <>
        <TooltipCustom
          tooltipText={isDisabled ? computedTextDisable : "Supprimer"}
          tooltipId={row.value}
        >
          <Button
            variant={"link"}
            className={isDisabled ? "" : "text-danger"}
            disabled={isDisabled}
            onClick={openDeleteModal}
          >
            <IconDelete />
            {title && <>&nbsp;Supprimer</>}
          </Button>
        </TooltipCustom>
        {!disable(row) && (
          <DeleteModal
            visible={visible}
            onCancel={closeDeleteModalCancel}
            onError={closeDeleteModalError}
            onSuccess={closeDeleteModalSuccess}
            query={query}
            ref={ref}
            onDelete={() => (reload ? reload() : window.location.reload())}
          />
        )}
      </>
    );
  },
  width: title ? 160 : 90,
  ...options,
});

export const ActionColumn = ({
  Header,
  accessor,
  sortField,
  buttons,
  width = 200,
  ...options
}: actionColumnType) => ({
  // eslint-disable-next-line react/display-name
  Header: Header,
  accessor: accessor,
  Cell: (row) => {
    return <ActionButton buttons={buttons} row={row} />;
  },
  sortField: sortField,
  width: buttons?.length * ACTION_BUTTON_SIZE ?? width,
  ...options,
});

export const BooleanColumn = ({
  Header,
  accessor,
  sortField,
  ...options
}: columnType) => ({
  // eslint-disable-next-line react/display-name
  Header: Header,
  accessor: accessor,
  Cell: (value) => {
    return (
      <div className="text-center">
        <Form.Check type="checkbox" disabled checked={value.value === true} />
      </div>
    );
  },
  sortField: sortField,
  ...options,
});

export const ProtectedColumn = ({
  Header,
  accessor,
  sortField,
  ...options
}: columnType) => ({
  // eslint-disable-next-line react/display-name
  Header: Header,
  accessor: accessor,
  Cell: (value: { value: any }) => {
    return (
      <>
        <div className="text-center">
          {value.value ? <IconLock /> : <IconUnlock />}
        </div>
      </>
    );
  },
  sortField: sortField,
  ...options,
});
